import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from './button';

export function useLocalPagination<T>(items: T[], pageSize = 10) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [items]);

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount));
  }, [pageCount]);

  const pageItems = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page, pageSize]);
  return { page, pageCount, pageItems, pageSize, setPage, totalItems: items.length };
}

export function useControlledPagination<T>(items: T[], page: number, onPageChange: (page: number) => void, pageSize = 10) {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  useEffect(() => {
    if (page > pageCount) onPageChange(pageCount);
  }, [onPageChange, page, pageCount]);

  const pageItems = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page, pageSize]);
  return { page, pageCount, pageItems, pageSize, setPage: onPageChange, totalItems: items.length };
}

export function PaginationControls({
  page,
  pageCount,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: {
  readonly page: number;
  readonly pageCount: number;
  readonly pageSize: number;
  readonly totalItems: number;
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange?: (pageSize: number) => void;
}) {
  const firstItem = (page - 1) * pageSize + 1;
  const lastItem = Math.min(totalItems, page * pageSize);

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-[#e6ebe1] pt-3 text-sm text-[#5f6b58]">
      <div className="flex flex-wrap items-center gap-2">
        <span>{totalItems === 0 ? '0' : `${firstItem}-${lastItem}`} de {totalItems}</span>
        {onPageSizeChange ? (
          <select
            className="h-8 rounded-md border border-[#d6ded0] bg-white px-2 text-sm text-[#334033]"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            aria-label="Registros por pagina"
          >
            {[10, 25, 50].map((size) => <option key={size} value={size}>{size} / pagina</option>)}
          </select>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="secondary" className="h-8 w-8 px-0" disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Página anterior">
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <span className="min-w-16 text-center">Página {page} de {pageCount}</span>
        <Button type="button" variant="secondary" className="h-8 w-8 px-0" disabled={page >= pageCount} onClick={() => onPageChange(page + 1)} aria-label="Página siguiente">
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
